import React from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Button, Tabs, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {LibraryTab, StudioAgentEntry, StudioCatalogue, StudioWorkflowEntry} from './useNovaStudioShell';
import {NovaStudioColor} from '../shared/novaStudioColors';
import {isDefaultCategoryName} from '../shared/categoryName';

const tokens = tokensForThemeProvider.sky.sys;

// The selected row carries the primary blue as a left bar on a light blue tint; a hovered row shows
// the bar alone. This panel is hand-rolled rather than built from DS list components, so its row
// styling reads straight from the theme tokens.
const ACCENT = tokens.color.border.active;
const ACCENT_TINT = tokens.color.background.primary['1'];

const Panel = styled.div`
	width: 264px;
	border-right: 1px solid ${NovaStudioColor.border};
	display: flex;
	flex-direction: column;
	min-height: 0;
`;
// The DS Tabs as they are, with each label kept on one line.
const LibraryTabBar = styled.div`
	padding: 0 16px;
	flex-shrink: 0;

	[role='tab'] {
		white-space: nowrap;
	}
`;
const TopBar = styled.div`
	padding: 16px;
	flex-shrink: 0;
`;
// Only the agent list scrolls; TopBar (New agent) stays pinned above it.
const ScrollList = styled.div`
	flex-grow: 1;
	min-height: 0;
	overflow-y: auto;
`;
// Shown when the whole portfolio is empty: a centered prompt pointing to New agent.
const EmptyPortfolio = styled.div`
	flex-grow: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
	padding: 24px;
	text-align: center;
	color: ${NovaStudioColor.textMuted};
`;
const CountBadge = styled.span`
	min-width: 20px;
	padding: 1px 7px;
	border-radius: 10px;
	background: ${NovaStudioColor.badgeBackground};
	color: ${NovaStudioColor.textMuted};
	font-size: 11px;
	font-weight: 600;
	text-align: center;
`;
const CategoryHeaderRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 8px 16px;
`;
const CategoryRight = styled.span`
	display: flex;
	align-items: center;
	gap: 8px;
`;
const CategoryName = styled.span`
	font-size: 12px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.02em;
	color: ${NovaStudioColor.textMuted};
`;
// Active/hover are shown with a left-accent bar (and a tint when selected) rather than a full
// background fill. The 3px left border is always present but transparent when idle, so the label never
// shifts. A <button> does not inherit the page font, so the body font is set here.
const AgentRow = styled.button<{$selected: boolean}>`
	display: block;
	width: 100%;
	text-align: left;
	padding: 8px 16px;
	border: none;
	border-left: 3px solid ${props => (props.$selected ? ACCENT : 'transparent')};
	cursor: pointer;
	${tokens.font.body['2']}
	color: ${props => (props.$selected ? tokens.color.text.content.main : NovaStudioColor.agentRowText)};
	background: ${props => (props.$selected ? ACCENT_TINT : 'transparent')};

	&:hover {
		border-left-color: ${ACCENT};
	}
`;

// A workflow row carries its bound record and page under the title, so it is a column rather than the
// single-line agent row.
const WorkflowRow = styled(AgentRow)`
	display: flex;
	flex-direction: column;
	gap: 2px;
`;
const WorkflowList = styled(ScrollList)`
	padding-top: 8px;
`;

const LIBRARY_TAB_LABEL_IDS: Record<LibraryTab, string> = {
	[LibraryTab.TaskAgents]: 'nova.catalogue.tab.taskAgents',
	[LibraryTab.AgenticWorkflows]: 'nova.catalogue.tab.agenticWorkflows',
};
const LIBRARY_TAB_ORDER: LibraryTab[] = [LibraryTab.TaskAgents, LibraryTab.AgenticWorkflows];

export interface DeleteCategoryPayload {
	categoryId: string;
	categoryName: string;
	agentCount: number;
}

interface Props {
	catalogue: StudioCatalogue;
	workflows: StudioWorkflowEntry[];
	libraryTab: LibraryTab;
	onLibraryTabChange: (tab: LibraryTab) => void;
	selectedAgentId: string | null;
	onSelectAgent: (agentId: string) => void;
	onCreateAgent: () => void;
	onDeleteCategory: (payload: DeleteCategoryPayload) => void;
}

type TaskAgentListProps = Pick<Props, 'catalogue' | 'selectedAgentId' | 'onSelectAgent' | 'onCreateAgent' | 'onDeleteCategory'>;

// Today's categorised task-agent list, unchanged: the pinned New agent, the empty state, and the
// category groups.
function TaskAgentList({
	catalogue,
	selectedAgentId,
	onSelectAgent,
	onCreateAgent,
	onDeleteCategory,
}: TaskAgentListProps): JSX.Element {
	const {formatMessage} = useIntl();
	// The empty state replaces the catalogue only when the portfolio is truly pristine: no agents and
	// no user-created categories (just the empty Uncategorised group). As soon as the author has any
	// agent, or has created any category, the grouped list is shown instead — an empty *category*
	// still needs its heading (with a 0 badge) so it can be seen and deleted, even before any agent
	// exists. Both halves are positive checks over what actually exists, so an empty `groups` array
	// can't assert "no agents" on its own.
	const hasAnyAgent = catalogue.groups.some(group => group.agents.length > 0);
	const hasUserCategory = catalogue.groups.some(group => !group.category.isUncategorised);
	const isEmpty = !hasAnyAgent && !hasUserCategory;
	return (
		<>
			{/* The pinned New agent button is only shown once the portfolio has content. While it is empty,
			    the centered empty state carries its own New agent button, so a second one above it is redundant. */}
			{!isEmpty && (
				<TopBar>
					<Button
						emphasis="high"
						fullWidth
						icon="plus"
						data-cy="nova-new-agent"
						data-testid="nova-new-agent"
						onPress={onCreateAgent}
					>
						{formatMessage({id: 'nova.catalogue.newAgentShort'})}
					</Button>
				</TopBar>
			)}
			{isEmpty ? (
				<EmptyPortfolio data-cy="nova-studio-catalogue-empty">
					<Text>{formatMessage({id: 'nova.catalogue.emptyTitle'})}</Text>
					<Text>
						{formatMessage({
							id: 'nova.catalogue.emptyBody',
						})}
					</Text>
					<Button emphasis="high" icon="plus" data-cy="nova-studio-catalogue-empty-new-agent" onPress={onCreateAgent}>
						{formatMessage({id: 'nova.catalogue.newAgentShort'})}
					</Button>
				</EmptyPortfolio>
			) : (
				<ScrollList data-cy="nova-studio-catalogue-scroll">
					{catalogue.groups.map(group => (
						<div key={group.category.categoryId} data-cy={`nova-category-${group.category.categoryId}`}>
							<CategoryHeaderRow>
								<CategoryName>{group.category.name}</CategoryName>
								<CategoryRight>
									<CountBadge>{group.agents.length}</CountBadge>
									{/* Uncategorised and the Default category cannot be deleted; the service refuses both. */}
									{!group.category.isUncategorised && !isDefaultCategoryName(group.category.name) && (
										<Button
											emphasis="low"
											icon="trash"
											iconOnly
											aria-label={formatMessage({
												id: 'nova.catalogue.deleteCategory',
											})}
											data-cy={`nova-delete-category-${group.category.categoryId}`}
											onPress={() =>
												onDeleteCategory({
													categoryId: group.category.categoryId,
													categoryName: group.category.name,
													agentCount: group.agents.length,
												})
											}
										/>
									)}
								</CategoryRight>
							</CategoryHeaderRow>
							{group.agents.map((agent: StudioAgentEntry) => (
								<AgentRow
									key={agent.agentId}
									$selected={agent.agentId === selectedAgentId}
									aria-current={agent.agentId === selectedAgentId ? 'true' : undefined}
									data-cy="nova-studio-agent-row"
									onClick={() => onSelectAgent(agent.agentId)}
								>
									{agent.name}
								</AgentRow>
							))}
						</div>
					))}
				</ScrollList>
			)}
		</>
	);
}

export function NovaStudioCatalogue({
	catalogue,
	workflows,
	libraryTab,
	onLibraryTabChange,
	selectedAgentId,
	onSelectAgent,
	onCreateAgent,
	onDeleteCategory,
}: Props): JSX.Element {
	const {formatMessage} = useIntl();
	return (
		<Panel data-cy="nova-studio-catalogue">
			<LibraryTabBar>
				<Tabs value={libraryTab} onValueChange={tab => onLibraryTabChange(tab as LibraryTab)}>
					<Tabs.List>
						{LIBRARY_TAB_ORDER.map(tab => (
							<Tabs.Tab key={tab} value={tab} data-cy={`nova-library-tab-${tab}`}>
								{formatMessage({id: LIBRARY_TAB_LABEL_IDS[tab]})}
							</Tabs.Tab>
						))}
					</Tabs.List>
				</Tabs>
			</LibraryTabBar>
			{libraryTab === LibraryTab.AgenticWorkflows ? (
				// Workflows are one per binding and created by engineering, so this list has no New agent.
				<WorkflowList data-cy="nova-studio-workflow-list">
					{workflows.map(workflow => (
						<WorkflowRow
							key={workflow.agentId}
							$selected={workflow.agentId === selectedAgentId}
							aria-current={workflow.agentId === selectedAgentId ? 'true' : undefined}
							data-cy="nova-studio-workflow-row"
							onClick={() => onSelectAgent(workflow.agentId)}
						>
							<span>{workflow.title}</span>
						</WorkflowRow>
					))}
				</WorkflowList>
			) : (
				<TaskAgentList
					catalogue={catalogue}
					selectedAgentId={selectedAgentId}
					onSelectAgent={onSelectAgent}
					onCreateAgent={onCreateAgent}
					onDeleteCategory={onDeleteCategory}
				/>
			)}
		</Panel>
	);
}
